// First-run onboarding: 4 short screens, Skip always visible, no scrolling on phones.

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Plus, Car } from "lucide-react";
import { track } from "../lib/analytics";

const SCREENS = [
  {
    icon: MapPin,
    title: "Find parking",
    text: "Tap \"Where to?\" to search a destination, and \"Layers\" to show live, free or paid parking around you.",
  },
  {
    icon: Plus,
    title: "Share a Spot",
    text: "One button, three ways to help: tell drivers you're leaving a spot, flag a free spot you saw, or correct parking information.",
  },
  {
    icon: Car,
    title: "My Car",
    text: "Save where you parked, get walking directions back to your car, and keep your vehicle details and reminder in one place.",
  },
];

export default function Onboarding({ open, onDone, replay = false }) {
  const [step, setStep] = React.useState(0);

  React.useEffect(() => {
    if (!open) return;
    setStep(0);
    track(replay ? "tutorial_replayed" : "onboarding_started", { type: "intro" });
  }, [open, replay]);

  const finish = (skipped) => {
    track(skipped ? "onboarding_skipped" : "onboarding_completed", { step: step + 1 });
    onDone(skipped);
  };

  const s = SCREENS[step];
  const Icon = s?.icon || MapPin;
  const last = step === SCREENS.length - 1;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[4000] bg-[#0a0a0c] flex flex-col"
          data-testid="onboarding"
        >
          <div className="flex items-center justify-between px-5 pt-[max(1rem,env(safe-area-inset-top))]">
            <div className="inline-flex items-center gap-2">
              <span className="w-7 h-7 rounded-lg bg-[#ccff00] flex items-center justify-center">
                <MapPin className="w-4 h-4 text-[#0a0a0c]" />
              </span>
              <span className="font-heading font-semibold text-white text-[15px]">SpotDrop.</span>
            </div>
            <button
              type="button"
              onClick={() => finish(true)}
              className="h-9 px-3 rounded-full text-[13px] text-zinc-400 border border-white/10"
              data-testid="onboarding-skip"
            >
              Skip
            </button>
          </div>

          <div className="flex-1 flex flex-col justify-center px-6">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.28 }}
              data-testid={`onboarding-screen-${step + 1}`}
            >
              <span className="w-14 h-14 rounded-2xl bg-[#ccff00]/10 border border-[#ccff00]/25 flex items-center justify-center">
                <Icon className="w-6 h-6 text-[#ccff00]" />
              </span>
              <h1 className="mt-5 font-heading text-3xl sm:text-4xl font-semibold text-white leading-tight">
                {s.title}
              </h1>
              <p className="mt-3 text-base text-zinc-400 leading-relaxed max-w-md">{s.text}</p>
            </motion.div>
          </div>

          <div className="px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
            <div className="flex items-center gap-1.5 mb-4" aria-hidden="true">
              {SCREENS.map((_, i) => (
                <span
                  key={i}
                  className={`h-1 rounded-full transition-all ${
                    i === step ? "w-6 bg-[#ccff00]" : "w-2 bg-white/15"
                  }`}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={() => (last ? finish(false) : setStep((v) => v + 1))}
              className="w-full h-12 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-[15px] active:scale-[0.98] transition-transform"
              data-testid={last ? "onboarding-start" : "onboarding-next"}
            >
              {last ? "Start using SpotDrop" : "Next"}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
